import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import { ApiFactory } from '../api/easypos-api';
import { IDayReport, IDaySummary } from '../api/model/requests';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { styles as sharedStyles } from '../styles/shared-styles';
import { designPrimitives } from '../styles/design-primitives';
import { resolveRouterPath, router as appRouter } from '../router';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// Z-report (Z3/Z4) — the shop-wide, day-scoped accounting report. Distinct from
// the per-register cash Z-report (which reconciles one drawer session); this
// nets the whole day's sales and refunds. A finalised day shows the immutable
// summary; a still-open day shows a throttled live running total (partial).
@customElement('daily-z-report-page')
export class DailyZReportPage extends Base {
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _report: IDayReport | null = null;
  @state() private _refreshing = false;

  private _loadedKey: string | null = null;
  private _gateTimer: number | null = null;

  static styles = [
    sharedStyles,
    designPrimitives,
    css`
      :host {
        display: block;
        max-width: 900px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      .report-head {
        display: flex;
        align-items: flex-start;
        gap: 14px;
        margin-bottom: 18px;
      }
      .report-head .icon-tile {
        margin-top: 2px;
      }
      .sub {
        color: var(--s-ink-soft, #666);
        margin: 0 0 16px;
        font-size: 0.9rem;
        display: flex;
        align-items: center;
        gap: 6px 10px;
        flex-wrap: wrap;
      }
      .live-note {
        margin: -6px 0 16px;
        padding: 8px 12px;
        border-radius: 10px;
        font-size: 0.82rem;
        background: var(--s-warning-soft, rgba(200, 140, 0, 0.1));
        color: var(--s-warning, rgb(150, 95, 0));
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }
      .live-note .refresh-btn {
        margin-left: auto;
      }
      .panel {
        margin-bottom: 14px;
      }
      .panel h3 {
        margin: 0 0 10px;
        font-size: 0.9rem;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--s-ink-soft, #666);
      }
      .balance-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
        font-variant-numeric: tabular-nums;
      }
      @media (min-width: 720px) {
        .balance-grid {
          grid-template-columns: repeat(4, 1fr);
        }
      }
      .cell {
        background: var(--s-canvas-soft, #f7f7f7);
        border: 1px solid transparent;
        border-radius: 12px;
        padding: 10px 12px;
      }
      .cell.strong {
        background: var(--s-canvas-strong, #eef);
        border-color: var(--s-line, rgba(15, 30, 55, 0.08));
      }
      .cell .label {
        font-size: 0.72rem;
        color: var(--s-ink-soft, #666);
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      .cell .val {
        font-size: 1.1rem;
        font-weight: 700;
        font-family: var(--font-display, inherit);
        margin-top: 2px;
      }
      .cell .val.neg {
        color: var(--s-danger, rgb(160, 25, 25));
      }
      table {
        width: 100%;
        border-collapse: collapse;
        font-variant-numeric: tabular-nums;
        font-size: 0.9rem;
      }
      th,
      td {
        padding: 7px 8px;
        border-bottom: 1px solid var(--s-line, #eee);
        text-align: left;
      }
      th {
        font-size: 0.78rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--s-ink-soft, #666);
      }
      td.right,
      th.right {
        text-align: right;
      }
      td.neg {
        color: var(--s-danger, rgb(160, 25, 25));
      }
      .back-link {
        margin-bottom: 8px;
      }
      .print-actions {
        margin-top: 14px;
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
      }
      @media print {
        :host {
          padding: 0;
          max-width: none;
          color: #000;
        }
        .back-link,
        .print-actions,
        sl-button,
        .live-note {
          display: none !important;
        }
        .icon-tile {
          display: none;
        }
        sl-card::part(base) {
          box-shadow: none;
          border: 1px solid #ccc;
        }
        .cell {
          background: #f5f5f5 !important;
          border-color: #ddd !important;
        }
      }
    `,
  ];

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._clearGateTimer();
  }

  // (Re)load from updated() keyed on shop + day id: load when the activeShop
  // context first arrives (absent on a hard nav straight to this URL) or when
  // the shop / day changes. The re-trigger deliberately does NOT gate on
  // _loading — that trap left a closed report stuck on "Loading…" forever once
  // the shop context arrived after the first paint.
  override updated(): void {
    const shop = this.activeShop?.shopName ?? null;
    if (!shop) return;
    const key = `${shop}|${this._dayId() ?? ''}`;
    if (key !== this._loadedKey) {
      this._loadedKey = key;
      void this._load();
    }
  }

  // The day id is the path segment between /shop/days/ and /z-report. Read it
  // from the URL, NOT router params: app-tools' router surfaces trailing route
  // params reliably, but this route's :id sits mid-path (.../:id/z-report), so
  // params.id isn't populated.
  private _dayId(): string | undefined {
    const m = window.location.pathname.match(/\/shop\/days\/([^/]+)\/z-report/);
    return m ? decodeURIComponent(m[1]) : undefined;
  }

  // Always resolves to data / "No data" / an error — never an indefinite
  // spinner: _loading is set inside the try and cleared in the finally on every
  // path, so "Loading…" only shows during an in-flight first fetch.
  private async _load(): Promise<void> {
    this._clearGateTimer();
    this._loading = true;
    this._error = null;
    try {
      const res = await this._fetchReport();
      if (!res.ok) {
        this._error = res.error;
        return;
      }
      this._report = res.data;
    } finally {
      this._loading = false;
    }
    this._armGate();
  }

  // Manual live refresh (open-day report only), gated to the server's live-cache
  // window: within that window the server hands back the same cached snapshot,
  // so a refresh would just burn a round-trip for identical numbers. Silent — it
  // keeps the current figures on screen and ignores a transient failure rather
  // than replacing live data with an error.
  private async _refresh(): Promise<void> {
    if (this._refreshing || !this._canRefresh()) return;
    this._refreshing = true;
    try {
      const res = await this._fetchReport();
      if (res.ok) this._report = res.data;
    } finally {
      this._refreshing = false;
    }
    this._armGate();
  }

  private async _fetchReport(): Promise<
    { ok: true; data: IDayReport } | { ok: false; error: string }
  > {
    if (!this.activeShop) return { ok: false, error: 'No active shop.' };
    const id = this._dayId();
    if (!id) return { ok: false, error: 'Missing day id.' };
    const api = ApiFactory.authenticated();
    const r = await api.getDayReport(this.activeShop.shopName, id);
    if (!r.success || !r.data)
      return { ok: false, error: r.error ?? 'Could not load the Z-report.' };
    return { ok: true, data: r.data };
  }

  // Seconds until the live snapshot ages past the server's cache window, at
  // which point a refresh would actually recompute. 0 means refresh is
  // available now. A finalised (non-partial) day never refreshes.
  private _refreshAvailableInSeconds(): number {
    const report = this._report;
    if (!report || !report.partial) return 0;
    const generatedMs = Date.parse(report.summary.generatedUtc);
    if (Number.isNaN(generatedMs)) return 0;
    const elapsed = (Date.now() - generatedMs) / 1000;
    return Math.max(0, Math.ceil(report.maxAgeSeconds - elapsed));
  }

  private _canRefresh(): boolean {
    return !!this._report?.partial && this._refreshAvailableInSeconds() === 0;
  }

  // One-shot LOCAL timer (no network) that re-renders when the refresh gate
  // opens, so the disabled button enables itself without the operator poking the
  // page. Cleared on disconnect and before each re-arm.
  private _armGate(): void {
    this._clearGateTimer();
    const secs = this._refreshAvailableInSeconds();
    if (!this._report?.partial || secs === 0) return;
    this._gateTimer = window.setTimeout(() => {
      this._gateTimer = null;
      this.requestUpdate();
    }, secs * 1000);
  }

  private _clearGateTimer(): void {
    if (this._gateTimer !== null) {
      clearTimeout(this._gateTimer);
      this._gateTimer = null;
    }
  }

  private _print() {
    window.print();
  }

  private _back() {
    history.length > 1
      ? history.back()
      : appRouter.navigate(resolveRouterPath('shop/days'));
  }

  private _cashReports() {
    appRouter.navigate(resolveRouterPath('cash/sessions'));
  }

  private _renderRefresh() {
    const secs = this._refreshAvailableInSeconds();
    const ready = secs === 0;
    const mins = Math.max(1, Math.ceil(secs / 60));
    return html`
      <sl-button
        class="refresh-btn"
        size="small"
        variant="default"
        ?disabled=${!ready || this._refreshing}
        ?loading=${this._refreshing}
        @click=${this._refresh}
      >
        <sl-icon slot="prefix" name="arrow-clockwise"></sl-icon>
        ${ready ? 'Refresh' : `Up to date · refresh in ~${mins}m`}
      </sl-button>
    `;
  }

  private _renderHeadline(s: IDaySummary) {
    return html`
      <div class="balance-grid">
        <div class="cell strong">
          <div class="label">Gross sales</div>
          <div class="val">${s.grossSales.toFixed(2)}</div>
        </div>
        <div class="cell strong">
          <div class="label">Refunds</div>
          <div class="val ${s.totalRefunds > 0 ? 'neg' : ''}">
            ${s.totalRefunds > 0 ? '−' : ''}${s.totalRefunds.toFixed(2)}
          </div>
        </div>
        <div class="cell strong">
          <div class="label">Net sales</div>
          <div class="val">${s.netSales.toFixed(2)}</div>
        </div>
        <div class="cell strong">
          <div class="label">Gross profit</div>
          <div class="val">${s.grossProfit.toFixed(2)}</div>
        </div>
        <div class="cell">
          <div class="label">Discounts</div>
          <div class="val">${s.totalDiscounts.toFixed(2)}</div>
        </div>
        <div class="cell">
          <div class="label">Tax</div>
          <div class="val">${s.tax.toFixed(2)}</div>
        </div>
        <div class="cell">
          <div class="label">Cost of goods</div>
          <div class="val">${s.costOfGoods.toFixed(2)}</div>
        </div>
        <div class="cell">
          <div class="label">Orders</div>
          <div class="val">
            ${s.completedCount}<span
              style="font-size:0.7rem;font-weight:500;color:var(--s-ink-soft,#666)"
            >
              ${s.cancelledCount > 0 ? ` · ${s.cancelledCount} cancelled` : ''}</span
            >
          </div>
        </div>
      </div>
    `;
  }

  render() {
    if (!this.activeShop) return html`<p>No active shop.</p>`;
    if (this._loading) return html`<p>Loading...</p>`;
    if (this._error) {
      return html`<sl-alert variant="danger" open>${this._error}</sl-alert>`;
    }
    const report = this._report;
    if (!report) return html`<p>No data.</p>`;
    const s = report.summary;
    const opened = new Date(s.openedUtc);
    const closed = s.closedUtc ? new Date(s.closedUtc) : null;
    const generated = new Date(s.generatedUtc);

    return html`
      <div class="back-link">
        <sl-button size="small" variant="text" @click=${this._back}
          >← Back</sl-button
        >
      </div>
      <div class="report-head">
        <span class="icon-tile">
          <sl-icon name="calendar-check"></sl-icon>
        </span>
        <div>
          <h1 class="page-head__title">Daily Z-Report</h1>
          <p class="sub">
            <strong>${s.displayLabel}</strong>
            · opened
            ${opened.toLocaleString()}${
              closed
                ? html` · closed ${closed.toLocaleString()}`
                : ' · still open'
            }
            ${
              report.partial
                ? html`<sl-badge variant="warning" pill>Live</sl-badge>`
                : html`<sl-badge variant="neutral" pill>Final</sl-badge>`
            }
          </p>
        </div>
      </div>

      ${
        report.partial
          ? html`<div class="live-note">
              <sl-icon name="broadcast"></sl-icon>
              <span
                >Live running total, as of ${generated.toLocaleTimeString()}.
                The day is still open, so this is a partial figure.</span
              >
              ${this._renderRefresh()}
            </div>`
          : nothing
      }

      <sl-card class="panel">
        <h3>Day totals</h3>
        ${this._renderHeadline(s)}
        <div class="print-actions">
          ${
            !report.partial
              ? html`<button class="btn-secondary-pill" @click=${this._print}>
                  <sl-icon name="printer"></sl-icon>
                  Print Z-report
                </button>`
              : nothing
          }
          <sl-button size="small" variant="text" @click=${this._cashReports}>
            <sl-icon slot="prefix" name="cash-stack"></sl-icon>
            Cash drawer Z-reports
          </sl-button>
        </div>
      </sl-card>

      <sl-card class="panel">
        <h3>Payment split</h3>
        ${
          s.paymentSplit.length === 0
            ? html`<p class="sub">No sales yet.</p>`
            : html`<table>
                <thead>
                  <tr>
                    <th>Method</th>
                    <th class="right">Orders</th>
                    <th class="right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${s.paymentSplit.map(
                    (p) => html`
                      <tr>
                        <td>${p.method}</td>
                        <td class="right">${p.saleCount}</td>
                        <td class="right">${p.total.toFixed(2)}</td>
                      </tr>
                    `
                  )}
                </tbody>
              </table>`
        }
      </sl-card>

      <sl-card class="panel">
        <h3>Refund split (${s.refundCount})</h3>
        ${
          s.refundSplit.length === 0
            ? html`<p class="sub">No refunds this day.</p>`
            : html`<table>
                <thead>
                  <tr>
                    <th>Method</th>
                    <th class="right">Count</th>
                    <th class="right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${s.refundSplit.map(
                    (rf) => html`
                      <tr>
                        <td>${rf.method}</td>
                        <td class="right">${rf.count}</td>
                        <td class="right">${rf.total.toFixed(2)}</td>
                      </tr>
                    `
                  )}
                </tbody>
              </table>`
        }
      </sl-card>

      ${
        s.shiftCash.length > 0
          ? html`<sl-card class="panel">
              <h3>Cash by shift</h3>
              <table>
                <thead>
                  <tr>
                    <th>Shift</th>
                    <th class="right">Opening</th>
                    <th class="right">Expected</th>
                    <th class="right">Counted</th>
                    <th class="right">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  ${s.shiftCash.map(
                    (sc) => html`
                      <tr>
                        <td>
                          ${sc.cashierLoginId}
                          <span
                            style="font-size:0.72rem;color:var(--s-ink-soft,#666)"
                            >·
                            ${sc.deviceId}${sc.endedUtc ? '' : ' · open'}</span
                          >
                        </td>
                        <td class="right">${sc.openingFloat.toFixed(2)}</td>
                        <td class="right">
                          ${sc.expectedClose == null ? '—' : sc.expectedClose.toFixed(2)}
                        </td>
                        <td class="right">
                          ${sc.countedClose == null ? '—' : sc.countedClose.toFixed(2)}
                        </td>
                        <td
                          class="right ${sc.variance != null && sc.variance < 0 ? 'neg' : ''}"
                        >
                          ${
                            sc.variance == null
                              ? '—'
                              : `${sc.variance > 0 ? '+' : ''}${sc.variance.toFixed(2)}`
                          }
                        </td>
                      </tr>
                    `
                  )}
                </tbody>
              </table>
            </sl-card>`
          : nothing
      }

      <sl-card class="panel">
        <h3>Top items</h3>
        ${
          s.topItems.length === 0
            ? html`<p class="sub">No items sold yet.</p>`
            : html`<table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th class="right">Qty</th>
                    <th class="right">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  ${s.topItems.map(
                    (it) => html`
                      <tr>
                        <td>${it.name}</td>
                        <td class="right">${it.quantity}</td>
                        <td class="right">${it.revenue.toFixed(2)}</td>
                      </tr>
                    `
                  )}
                </tbody>
              </table>`
        }
      </sl-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'daily-z-report-page': DailyZReportPage;
  }
}
